import { FileTextIcon } from 'lucide-react'

import { Badge, IconLink } from 'slab'
import { type MoneyAmount } from 'types'
import { type UserFacingEnrollmentMetadata } from 'types/member_onboarding_v2'
import { formatMoney, getFormattedDate } from 'utils'

import { getPaymentType } from '../PostEnrollmentActions'
import { calculateTieredAllowanceBreakdown } from '../utils'
import { MetalTierBadge } from './MetalTierBadge/MetalTierBadge'

interface SummarySidebarProps {
  enrollments?: Array<UserFacingEnrollmentMetadata>;
  coverageStartDate: Date;
  allowanceAmount: MoneyAmount;
  totalPremiumsAmount: MoneyAmount;
  isMemberOnTieredAllowance: boolean;
}

const getPlanTypeLabel = (enrollment: UserFacingEnrollmentMetadata) => {
  if (enrollment.covers_medical) {
    return 'Medical'
  } else if (enrollment.covers_vision) {
    return 'Vision'
  } else {
    return 'Dental'
  }
}

const EnrollmentCards = ({
  enrollments,
}: {
  enrollments: UserFacingEnrollmentMetadata[];
}) => {
  return (
    <div className='flex flex-col items-start self-stretch gap-y-2 mt-2'>
      {enrollments.map((enrollment, index) => {
        const planType = getPlanTypeLabel(enrollment)
        const planName = enrollment.plan_name
        const premiumValue = enrollment.premium_amount
          ? parseFloat(enrollment.premium_amount.value)
          : null
        const hasBadges =
          enrollment.medical_metal_tier || enrollment.medical_plan_type

        return (
          <div
            key={index}
            className='flex flex-col gap-y-1 items-start self-stretch'
          >
            <span className='body-bold'>{planType}</span>
            <div className='flex flex-col items-start self-stretch p-4 gap-2 bg-white border border-gray-200 rounded-lg'>
              <div className='flex items-start self-stretch gap-2'>
                {enrollment.carrier_img_path && (
                  <div className='border border-gray-200 rounded-md p-1'>
                    <img
                      src={enrollment.carrier_img_path}
                      alt={enrollment.carrier_name}
                      className='w-14 h-10 object-contain'
                    />
                  </div>
                )}
                <div className='flex flex-col items-start flex-1 gap-1'>
                  <span className='body-sm-bold'>{planName}</span>
                  <div className='flex items-start self-stretch justify-between'>
                    <span className='body-sm text-gray-500'>
                      {enrollment.carrier_name}
                    </span>
                    {!hasBadges && (
                      <span className='body-sm text-right'>
                        {premiumValue ? formatMoney(premiumValue) : ''}
                      </span>
                    )}
                  </div>
                </div>
                {enrollment.plan_id && enrollment.covers_medical && (
                  <IconLink
                    href={`/public/plans/${enrollment.plan_id}?premium=${premiumValue}`}
                    target='_blank'
                    rel='noopener noreferrer'
                    title='View plan details'
                    icon={FileTextIcon}
                    label='View plan details'
                    style={{ marginRight: '-8px', marginTop: '-8px' }}
                  />
                )}
              </div>
              {hasBadges && (
                <div className='flex items-center self-stretch justify-between gap-2'>
                  <div className='flex items-center gap-2'>
                    {enrollment.medical_metal_tier && (
                      <MetalTierBadge
                        bordered
                        tier={enrollment.medical_metal_tier}
                      />
                    )}
                    {enrollment.medical_plan_type && (
                      <Badge type='neutral' bordered>
                        {enrollment.medical_plan_type.toUpperCase()}
                      </Badge>
                    )}
                  </div>
                  <span className='body-sm text-right'>
                    {premiumValue ? formatMoney(premiumValue) : ''}
                  </span>
                </div>
              )}
            </div>
          </div>
        )
      })}
    </div>
  )
}

export const SummarySidebar = ({
  enrollments = [],
  coverageStartDate,
  allowanceAmount,
  totalPremiumsAmount,
  isMemberOnTieredAllowance,
}: SummarySidebarProps) => {
  const allowanceValue = parseFloat(allowanceAmount.value)
  const totalPremiumsValue = parseFloat(totalPremiumsAmount.value)
  const thatchBalance = allowanceValue - totalPremiumsValue

  const thatchResponsibleEnrollments = enrollments.filter(
    (enrollment) => getPaymentType(enrollment) === 'on_thatch'
  )
  const memberResponsibleEnrollments = enrollments.filter(
    (enrollment) => getPaymentType(enrollment) !== 'on_thatch'
  )

  let employerPays = ''
  let youPay = ''
  if (isMemberOnTieredAllowance) {
    const { formattedEmployerContribution, formattedAmount } = calculateTieredAllowanceBreakdown({
      allowanceAmount,
      medicalEnrollment: enrollments[0]
    })
    employerPays = formattedEmployerContribution
    youPay = formattedAmount
  }

  return (
    <div className='pt-16 md:pt-20 lg:pt-28 pb-8 md:pb-10 lg:pb-12 px-6 md:px-8 lg:px-10 w-full'>
      <div className='flex flex-col gap-y-4 md:gap-y-6'>
        <h3 className='heading-sm'>Summary</h3>

        <div className='h-[1px] bg-gray-200' />

        <div className='flex flex-col gap-y-2'>
          <p className='text-gray-800 body-sm'>
            Coverage starts{' '}
            <span className='body-sm-bold'>
              {getFormattedDate(coverageStartDate.toISOString(), {
                month: 'long',
                day: 'numeric',
                year: 'numeric',
                timeZone: 'UTC',
              })}
            </span>
          </p>

          {thatchResponsibleEnrollments.length > 0 && (
            <>
              <p className='body-sm-bold'>Thatch will pay for:</p>
              <EnrollmentCards enrollments={thatchResponsibleEnrollments} />
            </>
          )}
        </div>

        {memberResponsibleEnrollments.length > 0 && (
          <div className='flex flex-col gap-y-2'>
            <p className='body-sm-bold'>You're responsible for:</p>
            <EnrollmentCards enrollments={memberResponsibleEnrollments} />
          </div>
        )}

        <div className='h-[1px] bg-gray-200' />

        {isMemberOnTieredAllowance
          ? (
            <div className='flex flex-col items-start self-stretch gap-2'>
              <div className='flex justify-between items-center self-stretch'>
                <p className='body-sm'>Total cost</p>
                <p className='body-sm'>{formatMoney(totalPremiumsValue)}</p>
              </div>

              <div className='flex justify-between items-center self-stretch'>
                <p className='body-sm'>Your employer pays</p>
                <p className='body-sm'>-{employerPays}</p>
              </div>

              <div className='flex justify-between items-center self-stretch'>
                <p className='body-bold'>You pay</p>
                <p className='body-bold'>{youPay}</p>
              </div>
            </div>
            )
          : (
            <div className='flex flex-col items-start self-stretch gap-2'>
              <div className='flex justify-between items-center self-stretch'>
                <p className='body-sm'>Total budget</p>
                <p className='body-sm'>{formatMoney(allowanceValue)}</p>
              </div>

              <div className='flex justify-between items-center self-stretch'>
                <p className='body-sm'>Total plan costs</p>
                <p className='body-sm'>-{formatMoney(totalPremiumsValue)}</p>
              </div>

              <div className='flex justify-between items-center self-stretch'>
                <p className='body-bold'>
                  {thatchBalance > 0 ? 'Thatch balance' : 'Your contribution'}
                </p>
                <p
                  className={`body-bold ${
                  thatchBalance > 0 ? 'text-green-400' : 'text-red-500'
                }`}
                >
                  {formatMoney(thatchBalance)}
                </p>
              </div>
            </div>
            )}
      </div>
    </div>
  )
}

export default SummarySidebar
